import { selectPartner360Data } from 'legacy/lut/features/partner360/configuration'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { ModalDialog } from 'core-components/library/modalDialog'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { CockpitIconButton } from 'partner360/features/financialCockpit/ui/components/cockpitIconButton'
import { PlanSectionDialog, PlanSectionHeaderActions } from 'partner360/features/planSectionDialog'

import { getPlanReviewGroup, getPlanReviewItem } from '../config/steps'
import { PLAN_REVIEW_TRANSLATIONS } from '../config/translations'
import { buildPlanReviewSectionUrl } from '../lib/buildPlanReviewSectionUrl'
import { PlanReviewSection, TPlanReviewOffering, TPlanReviewRole } from '../model/types'
import { usePlanReviewJustifications } from '../model/usePlanReviewJustifications'

import { PlanReviewDialogContent } from './PlanReviewDialogContent'
import { PlanReviewModalContent } from './PlanReviewModalContent'
import { PlanReviewText } from './PlanReviewText'

import styles from './PlanReviewDialog.module.scss'

interface IPlanReviewDialogProps {
  show: boolean
  partnerId: string
  fYear: number
  programType: TPlanReviewOffering
  isGlobal?: boolean
  role: TPlanReviewRole
  /** Translation key of the button that opened the dialog, repeated on its continue button. */
  continueCaption?: string
  onClose: () => void
  onContinue: () => void
  onReject: () => void
  showActions?: boolean
  showJustifications?: boolean
  /** Enables the acknowledgment-only Summary entry point. */
  showSummary?: boolean
  /** `cockpit` opens the review the way the cockpit opens its plan sections: over the whole screen
   *  in the section window, its header the cockpit card's, under the plan deck. */
  frame?: 'modal' | 'cockpit'
  /** The cockpit frame's line under the title: the partner, its PID, the offering and the year. */
  description?: ReactNode
  /** The reviewed partner's identity, including when opened away from that partner's page. */
  partner?: {
    name?: string
    isDistributor?: boolean
  }
}

export const PlanReviewDialog = ({
  show,
  partnerId,
  fYear,
  programType,
  isGlobal = false,
  role,
  continueCaption = 'partnercommitment-sign-off',
  onClose,
  onContinue,
  onReject,
  showActions = true,
  showJustifications = true,
  showSummary = false,
  frame = 'modal',
  description,
  partner,
}: IPlanReviewDialogProps) => {
  const t = useSafetyTranslation()
  const isSummaryEnabled = showSummary && showJustifications
  const initialSection = isSummaryEnabled
    ? PlanReviewSection.Summary
    : PlanReviewSection.FinancialGrowth
  const [section, setSection] = useState(initialSection)
  const user = useSelector(selectUser)
  const pagePartner = useSelector(selectPartner360Data)
  // Never label a review for another partner with the current page's identity.
  const isPagePartner = pagePartner.partnerId === partnerId && pagePartner.fYear === fYear
  const isDistributor =
    partner?.isDistributor ?? (isPagePartner && pagePartner.partnerAccess.isDistributor)
  const partnerName = partner?.name || (isPagePartner && pagePartner.partnerName) || partnerId
  // A distributor's acknowledgment covers every offering, so only the partner and year are named.
  const planDescription = [
    partnerName,
    !isDistributor && programType,
    `FY${String(fYear).slice(-2)}`,
  ]
    .filter(Boolean)
    .join(' \u00b7 ')
  const userFullName = useMemo(
    () => [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.email,
    [user.email, user.firstName, user.lastName]
  )
  const {
    justifications,
    isLoading: isJustificationsLoading,
    isSaving: isJustificationSaving,
    loadError: justificationsLoadError,
    saveError: justificationSaveError,
    reload: reloadJustifications,
    save: saveJustification,
  } = usePlanReviewJustifications({
    enabled: show && showJustifications,
    partnerId,
    fYear,
    programType,
    isGlobal,
    userEmail: user.email,
    userFullName,
  })

  // Reset on close rather than on open: the content picks the section to start from once the
  // quality is loaded, and a reset effect here would run after it and override that choice.
  useEffect(() => {
    if (!show) {
      setSection(initialSection)
    }
  }, [initialSection, show])

  const isSummary = section === PlanReviewSection.Summary
  const group = getPlanReviewGroup(section)
  const item = getPlanReviewItem(section)
  const sectionLinkLabel =
    section === PlanReviewSection.PlanningMeasure
      ? t(PLAN_REVIEW_TRANSLATIONS.planningMeasure.openFinancialSection.key, {
          defaultValue: PLAN_REVIEW_TRANSLATIONS.planningMeasure.openFinancialSection.fallback,
        })
      : group?.key === 'financial'
        ? 'Open Financial section'
        : group?.key === 'initiatives'
          ? 'Open Initiative section'
          : 'Open section'
  /* A review across every offering has no one section a fix is made in. */
  const sectionUrl = useMemo(
    () =>
      isSummary || programType === null
        ? null
        : buildPlanReviewSectionUrl({
            section,
            partnerId,
            fYear,
            programType,
          }),
    [fYear, isSummary, partnerId, programType, section]
  )
  const contentProps = {
    partnerId,
    fYear,
    programType,
    isGlobal,
    role,
    continueCaption,
    section,
    justifications,
    isJustificationsLoading,
    isJustificationSaving,
    justificationsLoadError,
    justificationSaveError,
    onSectionChange: setSection,
    onReloadJustifications: reloadJustifications,
    onSaveJustification: saveJustification,
    onContinue,
    onReject,
    showActions,
    showJustifications,
    showSummary: isSummaryEnabled,
  }

  const title = isSummary ? (
    <PlanReviewText text={PLAN_REVIEW_TRANSLATIONS.summary.header} />
  ) : section === PlanReviewSection.PlanningMeasure ? (
    <PlanReviewText text={PLAN_REVIEW_TRANSLATIONS.planningMeasure.header} />
  ) : (
    <>
      {group && <Translation text={group.title} span />}
      {group && item && ' - '}
      {item && <Translation text={item.title} span />}
    </>
  )

  if (frame === 'cockpit') {
    return (
      <PlanSectionDialog
        show={show}
        title={item?.title ?? group?.title ?? ''}
        heading={title}
        description={description}
        onClose={onClose}
      >
        {sectionUrl && (
          <PlanSectionHeaderActions>
            <CockpitIconButton
              showLabel
              icon="kbase kbase-external-link"
              label={sectionLinkLabel}
              href={sectionUrl}
              target="_blank"
            />
          </PlanSectionHeaderActions>
        )}
        <div className={styles.cockpitFrame}>
          <PlanReviewDialogContent {...contentProps} closeModal={onClose} />
        </div>
      </PlanSectionDialog>
    )
  }

  return (
    <ModalDialog
      show={show}
      onClose={onClose}
      closeOnOutsideClick={false}
      size="lg"
      classes={{
        window: styles.modalWindow,
        body: styles.modalBody,
        header: styles.modalHeader,
      }}
      content={PlanReviewModalContent}
      contentProps={{
        title,
        description: planDescription,
        sectionUrl,
        sectionLinkLabel,
        reviewProps: contentProps,
      }}
    />
  )
}
